// @ts-check

/**
 * @file Renders a connection-error screen shown at boot when the
 * backend is unreachable. Sits in front of every route — including
 * sign-in — because none of them work without the backend.
 */

import _ from "gettext-universal/build/src/translate.js"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, Text, View} from "react-native"
import {View as PropforgeView} from "react-native-propforge"

import errorMessage from "../shared/error-message"
import {retryCurrentUser} from "./use-current-user"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
/** @type {Record<string, object>} */
const dataSets = {}

/**
 * @typedef {object} ConnectionErrorScreenProps
 * @property {Error} error Connection error captured during boot.
 */

/**
 * @typedef {object} ConnectionErrorScreenState
 * @property {boolean} retrying Whether a retry attempt is in flight.
 */

/**
 * Boot-time connection-error screen. Replaces both the sign-in form
 * and the authenticated chrome until the backend is reachable, since
 * neither can do anything useful without a connection.
 * @augments {ShapeComponent<ConnectionErrorScreenProps, ConnectionErrorScreenState>}
 */
class ConnectionErrorScreen extends ShapeComponent {
  /** @type {ConnectionErrorScreenState} */
  state = {retrying: false}

  /** Initializes translations for the screen. */
  setup() {
    useLocale()
  }

  /**
   * Re-runs the boot profile fetch.
   * @returns {Promise<void>} Resolves when the retry attempt finishes.
   */
  onRetryPress = async () => {
    if (this.s.retrying) return
    this.s.retrying = true
    try {
      await retryCurrentUser()
    } finally {
      this.s.retrying = false
    }
  }

  /**
   * Renders the connection-error screen.
   * @returns {ReactNode} Screen content.
   */
  render() {
    const {error} = this.p
    const {retrying} = this.s
    return (
      <PropforgeView
        dataSet={dataSets.systemTestingComponent ||= {
          focussed: "true"
        }}
        style={styles.systemTestingComponent ||= {
          flex: 1
        }}
        testID="systemTestingComponent"
      >
      <View
        style={styles.screen ||= {
          alignItems: "center",
          backgroundColor: "#f8fafc",
          flex: 1,
          justifyContent: "center",
          padding: 24
        }}
        testID="connectionErrorScreen"
      >
        <View
          style={styles.card ||= {
            backgroundColor: "#ffffff",
            borderColor: "#fecaca",
            borderRadius: 16,
            borderWidth: 1,
            gap: 12,
            maxWidth: 480,
            padding: 24,
            width: "100%"
          }}
          testID="connectionErrorScreen/card"
        >
          <Text
            style={styles.title ||= {
              color: "#991b1b",
              fontSize: 20,
              fontWeight: 700
            }}
            testID="connectionErrorScreen/title"
          >
            {_("Cannot reach the backend")}
          </Text>
          <Text
            style={styles.body ||= {
              color: "#374151",
              fontSize: 14,
              lineHeight: 20
            }}
            testID="connectionErrorScreen/body"
          >
            {_("The app couldn't load because the backend is unreachable. Check your internet connection or try again in a moment.")}
          </Text>
          <Text
            style={styles.detail ||= {
              color: "#6b7280",
              fontFamily: "monospace",
              fontSize: 12
            }}
            testID="connectionErrorScreen/detail"
          >
            {errorMessage(error)}
          </Text>
          <Pressable
            disabled={retrying}
            onPress={this.tt.onRetryPress}
            style={styles[`retryButton-${retrying}`] ||= {
              alignItems: "center",
              alignSelf: "flex-start",
              backgroundColor: retrying ? "#cbd5e1" : "#0f172a",
              borderRadius: 8,
              flexDirection: "row",
              paddingHorizontal: 16,
              paddingVertical: 10
            }}
            testID="connectionErrorScreen/retryButton"
          >
            <View
              style={styles.retryButtonIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="connectionErrorScreen/retryButton/icon"
            >
              <FontAwesome6 color="#ffffff" name="rotate-right" size={14} />
            </View>
            <Text
              style={styles.retryButtonText ||= {
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {retrying ? _("Retrying...") : _("Retry")}
            </Text>
          </Pressable>
        </View>
      </View>
      </PropforgeView>
    )
  }
}

/** @type {NamedExoticComponent<ConnectionErrorScreenProps>} */
const MemoizedConnectionErrorScreen = memo(shapeComponent(ConnectionErrorScreen))

export default MemoizedConnectionErrorScreen
